/**
 * Data: what the model reads and the values a user may set around it.
 *
 * - The catalogue identity and compiler profile, and the authoritative source
 *   fields the current revision publishes.
 * - Per-model settings (only a user's own editable model can be personalised).
 * - Property overrides, when the area is opened for one property
 *   (`/deal-models/$modelId/data?property=<uuid>`): the inputs whose source
 *   declares a `propertyOverrideKey`, saved against the matching Financial
 *   profile with the expected revision and source identity.
 *
 * Settings and override rows use the same descriptor-driven fields as
 * Settings → Investor / Home buyer (pounds, percentages, units, booleans),
 * reporting exact API values; a read-only model shows them disabled.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { IconDeviceFloppy } from '@tabler/icons-react'
import {
  useDealModelCatalogue,
  useDealModelCurrent,
  useDealModelSettings,
  useDealsV2PropertyModels,
  useDealsV2PropertyOverrides,
  usePutDealModelSettings,
  useSaveDealsV2PropertyOverrides,
  type DealModelSummary,
  type DealModelValueDescriptor,
} from '../../api/dealModels'
import { useAccess } from '../../api/queries'
import { Alert, Button, Checkbox, Empty, Skeletons } from '../../ui/kit'
import { SettingsField } from '../settings/SettingsFields'
import { settingsFieldPresentation } from '../settings/settingsFieldPresentation'
import { recipePropertyOverrides } from './propertyOverrides'
import { shortIdentity } from './WorkspaceHead'

type Value = string | boolean | null

// Kept local: importing the composer's DSL here would pull CodeMirror into the entry chunk.
const SEMANTIC_TYPES: ReadonlyArray<DealModelValueDescriptor['semanticType']> = ['money', 'moneyPence', 'decimal', 'ratio', 'percentage', 'integer', 'count', 'months', 'days', 'areaSqm', 'boolean', 'enum', 'string', 'date']
const isSemanticType = (value: string): value is DealModelValueDescriptor['semanticType'] => (SEMANTIC_TYPES as readonly string[]).includes(value)

function overrideDisplayValues(values: Record<string, unknown>): Record<string, Value> {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === null || typeof value === 'boolean' ? value : String(value)]))
}

export function DealModelDataCoverage({ model }: { model: DealModelSummary }) {
  const canPersonalize = model.sourceKind === 'user' && model.capabilities.edit
  const access = useAccess()
  const canCreateCustomModel = access.capability('deal_model.custom.create')
  const search = useSearch({ strict: false }) as { property?: string }
  const propertyId = typeof search.property === 'string' ? search.property : ''
  const draft = useDealModelCurrent(model.logicalModelId, Boolean(propertyId))
  const catalogue = useDealModelCatalogue(model.logicalModelId, model.revision)
  const settings = useDealModelSettings(model.logicalModelId)
  const saveSettings = usePutDealModelSettings(model.logicalModelId)
  const propertyModels = useDealsV2PropertyModels(propertyId, Boolean(propertyId))
  const profileId = propertyModels.data?.models.find((item) => item.logicalModelId === model.logicalModelId)?.profileId ?? ''
  const overrides = useDealsV2PropertyOverrides(profileId, propertyId, Boolean(profileId))
  const saveOverrides = useSaveDealsV2PropertyOverrides()
  const [settingValues, setSettingValues] = useState<Record<string, Value>>({})
  const [overrideValues, setOverrideValues] = useState<Record<string, Value>>({})
  const settingEdits = useRef({ generation: 0, dirty: false, revision: '0' })
  const overrideEdits = useRef({ scope: '', generation: 0, dirty: false, revision: '', sourceSha256: '' })
  const overrideScope = `${profileId}:${propertyId}`

  useEffect(() => {
    if (!settings.data || settingEdits.current.dirty) return
    settingEdits.current.revision = settings.data.settingsRevision
    setSettingValues(settings.data.values)
  }, [settings.data])
  useEffect(() => {
    if (!overrides.data || (overrideEdits.current.scope === overrideScope && overrideEdits.current.dirty)) return
    overrideEdits.current.scope = overrideScope
    overrideEdits.current.dirty = false
    overrideEdits.current.revision = overrides.data.revision
    overrideEdits.current.sourceSha256 = overrides.data.sourceSha256
    setOverrideValues(overrideDisplayValues(overrides.data.values))
  }, [overrides.data, overrideScope])

  const overrideFields = recipePropertyOverrides(draft.data?.sourceDocument)
  const markOverrideEdited = () => {
    overrideEdits.current.scope = overrideScope
    overrideEdits.current.generation += 1
    overrideEdits.current.dirty = true
  }
  const saveSettingValues = () => {
    if (!settings.data) return
    const generation = settingEdits.current.generation
    saveSettings.mutate({ expectedRevision: settingEdits.current.revision, values: settingValues, descriptors: settings.data.valueDescriptors }, {
      onSuccess: (saved) => {
        settingEdits.current.revision = saved.settingsRevision
        if (settingEdits.current.generation !== generation) return
        settingEdits.current.dirty = false
        setSettingValues(saved.values)
      },
    })
  }
  const saveOverrideValues = () => {
    if (!overrides.data) return
    const generation = overrideEdits.current.generation
    const scope = overrideScope
    saveOverrides.mutate({
      profileId,
      propertyId,
      expectedRevision: overrideEdits.current.revision,
      expectedSourceSha256: overrideEdits.current.sourceSha256,
      values: Object.fromEntries(overrideFields.filter((item) => Object.hasOwn(overrideValues, item.key)).map((item) => [item.key, overrideValues[item.key]])),
    }, {
      onSuccess: (saved) => {
        if (overrideEdits.current.scope !== scope) return
        overrideEdits.current.revision = saved.revision
        overrideEdits.current.sourceSha256 = saved.sourceSha256
        if (overrideEdits.current.generation !== generation) return
        overrideEdits.current.dirty = false
        setOverrideValues(overrideDisplayValues(saved.values))
      },
    })
  }

  if (catalogue.isPending) return <div className="page dm-page"><Skeletons count={1} kind="block" /><Skeletons count={6} kind="row" /></div>
  if (catalogue.isError) return <Alert title="Model data unavailable">{catalogue.error.message}</Alert>
  const data = catalogue.data

  return (
    <div className="page dm-page" data-testid="deal-model-data-coverage">
      {model.sourceKind === 'system' ? (
        <Alert info tight title="Shared built-in calculation">
          Built-in calculations use shared assumptions for everyone.
          {canCreateCustomModel ? <> <Link to="/deal-models/$modelId/editor" params={{ modelId: model.logicalModelId }} search={{}} className="dm-link">Open model to create a custom copy</Link></> : null}
        </Alert>
      ) : null}

      <div className="figs dm-figs">
        <div className="fig k"><small>Catalogue</small><b>{data.catalogueVersion}</b><span className="g2 t-mono" title={data.catalogueSha256}>{shortIdentity(data.catalogueSha256)}</span></div>
        <div className="fig"><small>Compiler profile</small><b>{data.compilerValidationProfile}</b><span className="g2">Revision {data.revision}</span></div>
      </div>

      <section className="dm-section" aria-label="Authoritative fields">
        <div className="dm-section-head">
          <h2 className="dm-title">Authoritative fields</h2>
          <span className="pill is-muted">{data.sourceFields.length} inputs · {data.outputFields.length} outputs</span>
        </div>
        {data.sourceFields.length === 0 ? <Empty title="No source fields">No source fields are visible in this scope.</Empty> : (
          <div className="dm-table-wrap">
            <table className="tbl dm-fields">
              <thead><tr><th>Field</th><th>Pointer</th><th>Type</th><th>Scope</th><th>Provenance</th></tr></thead>
              <tbody>{data.sourceFields.map((field) => (
                <tr key={field.sourceId}>
                  <td><span className="dm-cell-main">{field.label}</span><code>{field.key}</code></td>
                  <td><code>{field.pointer}</code></td>
                  <td><span className="dm-cell-main">{field.semanticType}{field.unit ? ` · ${field.unit}` : ''}</span><small>{field.nullable ? 'Nullable' : 'Required'}</small></td>
                  <td>{field.scope}</td>
                  <td>{field.provenanceKind}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="dm-section" aria-label="Per-model settings">
        <div className="dm-section-head">
          <div><h2 className="dm-title">Per-model settings</h2><p className="dm-note">These values apply to this model.</p></div>
          {settings.data ? <Button kind="primary" disabled={!canPersonalize} busy={saveSettings.isPending} onClick={saveSettingValues}><IconDeviceFloppy className="ic-sm" />Save model settings</Button> : null}
        </div>
        {settings.isPending ? <Skeletons count={3} kind="row" /> : null}
        {settings.isError ? <Alert tight title="Model settings unavailable">{settings.error.message}</Alert> : null}
        {saveSettings.isError ? <Alert tight title="Model settings were not saved">{saveSettings.error.message}</Alert> : null}
        {settings.data ? (
          <div className="panel dm-panel">
            {Object.keys(settingValues).length === 0 ? <p className="dm-note">No per-model settings are configured.</p> : (
              <fieldset className="dm-fieldset" disabled={!canPersonalize}>
                {settings.data.valueDescriptors.filter((descriptor) => Object.hasOwn(settingValues, descriptor.key)).map((descriptor) => (
                  <SettingsField
                    key={descriptor.key}
                    descriptor={descriptor}
                    value={settingValues[descriptor.key]}
                    onChange={(key, value) => {
                      settingEdits.current.generation += 1
                      settingEdits.current.dirty = true
                      setSettingValues((current) => ({ ...current, [key]: value }))
                    }}
                  />
                ))}
              </fieldset>
            )}
          </div>
        ) : null}
      </section>

      <section className="dm-section" aria-label="Property overrides">
        <div className="dm-section-head"><h2 className="dm-title">Property overrides</h2></div>
        {!propertyId ? <p className="dm-note">Open this area from a property result to inspect that property's authorized overrides.</p> : null}
        {propertyId && propertyModels.isError ? <Alert tight title="Financial profile unavailable">{propertyModels.error.message}</Alert> : null}
        {propertyId && propertyModels.data && !profileId ? <Alert tight warn title="No Financial profile">This model has no saved Financial profile for this property.</Alert> : null}
        {overrides.isPending && profileId ? <Skeletons count={3} kind="row" /> : null}
        {overrides.isError ? <Alert tight title="Property overrides unavailable">{overrides.error.message}</Alert> : null}
        {saveOverrides.isError ? <Alert tight title="Property overrides were not saved">{saveOverrides.error.message}</Alert> : null}
        {draft.isError ? <Alert tight title="Override fields unavailable">{draft.error.message}</Alert> : null}
        {overrides.data?.storedSourceSha256 && overrides.data.storedSourceSha256 !== overrides.data.sourceSha256 ? <Alert tight warn title="Model changed">Review these property overrides before saving them against the current model source.</Alert> : null}
        {overrides.data ? (
          <div className="panel dm-panel">
            <p className="dm-note">Enable a value to override it for this property. Clear its checkbox to use the model's normal input resolution.</p>
            {overrideFields.map((item) => {
              const enabled = Object.hasOwn(overrideValues, item.key)
              const descriptor: DealModelValueDescriptor = {
                key: item.key,
                semanticType: isSemanticType(item.kind) ? item.kind : 'string',
                nullable: item.nullable,
                enumValues: item.enumValues,
              }
              return (
                <div key={item.key} className="dm-override">
                  <Checkbox
                    label={`Override ${item.label}`}
                    checked={enabled}
                    disabled={!canPersonalize}
                    onChange={(checked) => {
                      markOverrideEdited()
                      setOverrideValues((current) => {
                        const next = { ...current }
                        if (checked) next[item.key] = item.defaultValue === null || typeof item.defaultValue === 'boolean' ? item.defaultValue : item.defaultValue === undefined ? '' : String(item.defaultValue)
                        else delete next[item.key]
                        return next
                      })
                    }}
                  />
                  <fieldset className="dm-fieldset" disabled={!enabled || !canPersonalize}>
                    <SettingsField
                      descriptor={descriptor}
                      presentation={{ ...settingsFieldPresentation(descriptor), label: item.label }}
                      value={overrideValues[item.key] ?? null}
                      onChange={(key, value) => { markOverrideEdited(); setOverrideValues((current) => ({ ...current, [key]: value })) }}
                    />
                  </fieldset>
                </div>
              )
            })}
            {overrideFields.length === 0 ? <p className="dm-note">This model has no inputs that permit property overrides.</p> : null}
            <div className="form-actions">
              <Button kind="primary" disabled={!canPersonalize || draft.isPending || draft.isError} busy={saveOverrides.isPending} onClick={saveOverrideValues}><IconDeviceFloppy className="ic-sm" />Save property overrides</Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  )
}
